import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Points,
  ShaderMaterial,
} from 'three';
import { Palette } from '../Palette.js';
import { GLSL_COMMON } from '../shaders/glslCommon.js';

/**
 * Several thousand stars as a single `Points` object: one draw call, one buffer,
 * zero per-frame CPU work.
 *
 * Twinkle, size attenuation and depth dimming are all evaluated in the shader from
 * `uTime` and per-star constants, so the vertex buffer is written once at build
 * time and never touched again. This is the pattern the whole view layer follows:
 * upload the invariants, derive the animation on the GPU.
 *
 * Stars are placed in a spherical shell rather than a cube — a cube reads as a
 * box once the camera drifts — with a fraction of them pulled toward a band so the
 * sky has structure instead of uniform static.
 */

export interface StarfieldOptions {
  readonly count?: number;
  readonly innerRadius?: number;
  readonly outerRadius?: number;
  /** Fraction of stars biased toward the galactic band, 0..1. */
  readonly bandFraction?: number;
  readonly seed?: number;
}

const VERTEX = /* glsl */ `
${GLSL_COMMON}

attribute float aSize;
attribute float aPhase;
attribute float aTwinkleRate;
attribute vec3  aColor;

uniform float uTime;
uniform float uPixelRatio;
uniform float uSizeScale;
uniform float uBreath;

varying vec3  vColor;
varying float vBrightness;

void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);

  // Two detuned sines per star: a single sine reads as a pulse, two read as
  // atmospheric scintillation because the beat period is long and irrational.
  float twinkle =
      sin(uTime * aTwinkleRate + aPhase) * 0.5 +
      sin(uTime * aTwinkleRate * 1.618 + aPhase * 2.3) * 0.5;
  float sparkle = 0.72 + 0.28 * twinkle;

  // Far stars dim rather than vanish, which keeps the shell from having an edge.
  float depth = -mvPosition.z;
  float distanceFade = 1.0 - smoothstep(120.0, 620.0, depth);

  vBrightness = sparkle * mix(0.55, 1.0, distanceFade) * (0.9 + 0.1 * uBreath);
  vColor = aColor;

  gl_PointSize = aSize * uPixelRatio * (uSizeScale / max(depth, 1.0)) * (0.85 + 0.15 * sparkle);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAGMENT = /* glsl */ `
varying vec3  vColor;
varying float vBrightness;

void main() {
  // A tight core plus a wide soft halo. The halo is what bloom picks up, so the
  // stars read as light sources rather than as dots.
  float d = length(gl_PointCoord - vec2(0.5));
  if (d > 0.5) discard;

  float core = smoothstep(0.13, 0.0, d);
  float halo = pow(smoothstep(0.5, 0.0, d), 2.6);

  vec3 color = vColor * (core * 1.7 + halo * 0.85) * vBrightness;
  gl_FragColor = vec4(color, 1.0);
}
`;

export class Starfield {
  readonly object: Points;
  private readonly material: ShaderMaterial;
  private readonly geometry: BufferGeometry;

  constructor(options: StarfieldOptions = {}) {
    const count = options.count ?? 6000;
    const inner = options.innerRadius ?? 70;
    const outer = options.outerRadius ?? 320;
    const bandFraction = options.bandFraction ?? 0.45;
    const random = mulberry32(options.seed ?? 0x5eed);

    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const phases = new Float32Array(count);
    const rates = new Float32Array(count);

    const scratch = new Color();

    for (let index = 0; index < count; index += 1) {
      // Uniform density in a shell needs the cube-root distribution; sampling the
      // radius linearly would crowd everything against the inner surface.
      const u = random();
      const radius = Math.cbrt(inner ** 3 + u * (outer ** 3 - inner ** 3));

      let direction = randomUnitVector(random);
      if (random() < bandFraction) {
        // Flatten toward the xz plane to suggest a galactic band.
        direction = normalize(direction[0], direction[1] * 0.18, direction[2]);
      }

      positions[index * 3] = direction[0] * radius;
      positions[index * 3 + 1] = direction[1] * radius;
      positions[index * 3 + 2] = direction[2] * radius;

      // Mostly small stars with a long tail of bright ones: a flat distribution
      // looks like noise, a power distribution looks like a sky.
      const magnitude = random();
      sizes[index] = 0.9 + Math.pow(magnitude, 5.5) * 7.5;

      const temperature = random();
      if (temperature < 0.62) scratch.copy(Palette.starCool);
      else if (temperature < 0.9) scratch.copy(Palette.starPale);
      else scratch.copy(Palette.starWarm);
      // Nudge each star off the three base colours so no banding is visible.
      scratch.offsetHSL((random() - 0.5) * 0.05, (random() - 0.5) * 0.15, 0);

      colors[index * 3] = scratch.r;
      colors[index * 3 + 1] = scratch.g;
      colors[index * 3 + 2] = scratch.b;

      phases[index] = random() * Math.PI * 2;
      rates[index] = 0.35 + random() * 1.5;
    }

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(positions, 3));
    this.geometry.setAttribute('aColor', new BufferAttribute(colors, 3));
    this.geometry.setAttribute('aSize', new BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new BufferAttribute(phases, 1));
    this.geometry.setAttribute('aTwinkleRate', new BufferAttribute(rates, 1));
    // The shell is static and centred; skipping the frustum test avoids a
    // bounding-sphere computation that would never cull anything anyway.
    this.geometry.boundingSphere = null;

    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: 1 },
        uSizeScale: { value: 190 },
        uBreath: { value: 0 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      // Fog would swallow the shell whole; stars dim by their own distance term.
      fog: false,
    });

    this.object = new Points(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.object.renderOrder = -50;
  }

  setPixelRatio(value: number): void {
    this.material.uniforms['uPixelRatio']!.value = value;
  }

  /**
   * Advances twinkle and applies a very slow drift.
   *
   * The rotation is deliberately below the threshold of conscious motion: it is
   * there so a still frame and a frame ten seconds later are never identical.
   */
  update(time: number, breath: number): void {
    this.material.uniforms['uTime']!.value = time;
    this.material.uniforms['uBreath']!.value = breath;
    this.object.rotation.y = time * 0.0042;
    this.object.rotation.x = Math.sin(time * 0.013) * 0.05;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

function randomUnitVector(random: () => number): [number, number, number] {
  // Marsaglia's method: rejection-free, and uniform on the sphere. Sampling
  // angles directly would bunch points at the poles.
  const z = random() * 2 - 1;
  const angle = random() * Math.PI * 2;
  const planar = Math.sqrt(1 - z * z);
  return [planar * Math.cos(angle), planar * Math.sin(angle), z];
}

function normalize(x: number, y: number, z: number): [number, number, number] {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
}

/** Small deterministic PRNG so the sky is identical on every reload. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
